import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { usePendingOnboardingAssets } from '@/hooks/usePendingOnboardingAssets';
import { isFeatureEnabledWithFallback } from '@/lib/featureEntitlements';
import { Loader2 } from 'lucide-react';

interface FeatureRouteProps {
  featureKey: string | null; // null means always accessible
  children: React.ReactNode;
  fallback?: string; // path to redirect to if feature is not enabled
  alsoAllowFeatureKey?: string;
  requireFeatureKey?: string;
  /**
   * Route-shaped placeholder to paint while the gate is still deciding.
   * Routes that have a skeleton matching their own UI should pass it so the
   * page layout appears once and stays put (see `AutomationsPageSkeleton`).
   */
  skeleton?: React.ReactNode;
}

export const FeatureRoute: React.FC<FeatureRouteProps> = ({
  featureKey,
  children,
  fallback = '/home',
  alsoAllowFeatureKey,
  requireFeatureKey,
  skeleton,
}) => {
  const location = useLocation();
  const { data: entitlements, isLoading } = useOrganizationEntitlements();
  const enabledFeatures = entitlements?.entitlements?.features || {};

  const isFeatureOn = (key: string) => isFeatureEnabledWithFallback(enabledFeatures, key);
  const isEnabled = (!featureKey || isFeatureOn(featureKey)) ||
    (!!alsoAllowFeatureKey && isFeatureOn(alsoAllowFeatureKey));
  const meetsRequiredFeature = !requireFeatureKey || isFeatureOn(requireFeatureKey);

  const isTemplatesRoute = location.pathname.startsWith('/home/templates');
  const isAutomationRoute = location.pathname.startsWith('/home/automation');
  const isBusinessAccountsRoute =
    location.pathname.startsWith('/home/business-accounts') ||
    location.pathname.startsWith('/home/integrations/whatsapp-business');

  // The onboarding assets only feed `allowOnboardingAccess` below - a bypass on
  // these three areas for a user the entitlements would turn away. Fetched only
  // then: every other guarded page (and any page the plan already allows) used
  // to request them on each visit and never read the answer.
  const mayNeedOnboardingBypass =
    (!!featureKey || !!requireFeatureKey) &&
    !isLoading &&
    !(isEnabled && meetsRequiredFeature) &&
    (isTemplatesRoute || isAutomationRoute || isBusinessAccountsRoute);
  const { data: pendingAssets, isLoading: pendingAssetsLoading } = usePendingOnboardingAssets({
    enabled: mayNeedOnboardingBypass,
  });

  const loadingView = skeleton ?? (
    <div className="flex min-h-[60vh] w-full items-center justify-center">
      <div className="flex items-center space-x-2">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span>Loading...</span>
      </div>
    </div>
  );

  if (!featureKey && !requireFeatureKey) {
    return <>{children}</>;
  }

  if (isLoading) {
    return <>{loadingView}</>;
  }

  // The onboarding-assets query only feeds `allowOnboardingAccess`, which is a
  // bypass for users whose entitlements say no. When entitlements already say
  // yes there is nothing left to decide, so don't hold the page for it.
  if (isEnabled && meetsRequiredFeature) {
    return <>{children}</>;
  }

  if (pendingAssetsLoading) {
    return <>{loadingView}</>;
  }

  const hasPendingTemplates = Array.isArray(pendingAssets?.pending_templates) && pendingAssets.pending_templates.length > 0;
  const hasPendingFlow = !!pendingAssets?.pending_flow;
  const hasActivatedOnboardingDrafts = !!pendingAssets?.activated_business_account_id;
  const allowOnboardingAccess =
    (isTemplatesRoute && (hasPendingTemplates || hasActivatedOnboardingDrafts)) ||
    (isAutomationRoute && (hasPendingFlow || hasActivatedOnboardingDrafts)) ||
    (isBusinessAccountsRoute && (hasPendingTemplates || hasPendingFlow || hasActivatedOnboardingDrafts));

  if ((!isEnabled && !allowOnboardingAccess) || !meetsRequiredFeature) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};




